import { X } from "lucide-react";
import { bookingPkg, fmtGBP, fmtDate } from "@/components/admin/BookingRow";

function Item({ label, children }) {
  return (
    <div>
      <div className="text-[10px] tracking-overline uppercase text-[#8A8782] mb-1">{label}</div>
      <div className="text-sm text-[#1F1E1D]">{children || "-"}</div>
    </div>
  );
}

function fmtTs(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleString("en-GB", {
    day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

export function BookingDetailsModal({ quote, onClose }) {
  const acc = quote.acceptance || {};
  const pkg = bookingPkg(quote);
  const paid = acc.deposit_paid;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="booking-details-modal"
    >
      <div className="bg-[#FAF9F6] max-w-2xl w-full p-8 md:p-10 relative border border-[#D1CEC2] max-h-[90vh] overflow-y-auto">
        <button
          onClick={onClose}
          className="absolute top-5 right-5 text-[#5C5A56] hover:text-[#1F1E1D]"
          data-testid="booking-details-close"
          aria-label="Close"
        >
          <X className="w-5 h-5" strokeWidth={1.5} />
        </button>
        <div className="text-xs tracking-overline uppercase text-[#A9927D] mb-2 font-medium">
          Booking Record
        </div>
        <h3 className="font-display text-3xl tracking-tight text-[#1F1E1D] mb-8">
          {acc.partner1_full_name || quote.partner1_name} &amp; {acc.partner2_full_name || quote.partner2_name}
        </h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5 mb-8">
          <Item label="Wedding Date">{fmtDate(quote.wedding_date || acc.wedding_date)}</Item>
          <Item label="Venue">{quote.venue_name || acc.wedding_venue}</Item>
          <Item label="Email">{quote.client_email}</Item>
          <Item label="Mobile">{acc.mobile_number}</Item>
          <Item label="Home Address">{acc.home_address}</Item>
          <Item label="Accepted On">{fmtTs(acc.accepted_at)}</Item>
        </div>

        <div className="border-t border-[#E5E3DB] pt-6 mb-8">
          <div className="text-[10px] tracking-overline uppercase text-[#8A8782] mb-3">
            Package Booked
          </div>
          {pkg ? (
            <>
              <div className="flex items-baseline justify-between mb-3">
                <div className="font-display text-2xl text-[#1F1E1D]">
                  {pkg.name}
                  {quote.no_socials && (
                    <span data-testid="booking-no-socials" className="ml-3 align-middle inline-block bg-[#8A3D3D] text-white text-[10px] tracking-overline uppercase font-medium px-2.5 py-1 rounded-full">
                      No Socials
                    </span>
                  )}
                </div>
                <div className="font-display text-xl text-[#8A6D1A]">{fmtGBP(pkg.price)}</div>
              </div>
              <ul className="space-y-1.5">
                {(pkg.features || []).map((f) => (
                  <li key={f} className="text-sm text-[#5C5A56]">• {f}</li>
                ))}
              </ul>
              {(acc.addons || []).length > 0 && (
                <div className="mt-4" data-testid="booking-addons">
                  <div className="text-[10px] tracking-overline uppercase text-[#8A8782] mb-1">Add-ons</div>
                  {(acc.addons || []).map((a) => (
                    <div key={a.key} className="text-sm text-[#8A6D1A]">
                      • {a.name} ({fmtGBP(a.price)})
                    </div>
                  ))}
                </div>
              )}
              {(acc.prewedding_shoot_included ||
                acc.video_guestbook_included ||
                (quote.video_booth_offer && /video/i.test(`${pkg?.name || ""} ${pkg?.subtitle || ""}`) && !/highlight/i.test(`${pkg?.name || ""} ${pkg?.subtitle || ""}`))) && (
                <div className="mt-4" data-testid="booking-prewedding">
                  <div className="text-[10px] tracking-overline uppercase text-[#8A8782] mb-1">Included Free</div>
                  {acc.prewedding_shoot_included && (
                    <div className="text-sm text-[#8A6D1A]">• Pre-Wedding Couple Photoshoot (60 minutes)</div>
                  )}
                  {(acc.video_guestbook_included ||
                    (quote.video_booth_offer && /video/i.test(`${pkg?.name || ""} ${pkg?.subtitle || ""}`) && !/highlight/i.test(`${pkg?.name || ""} ${pkg?.subtitle || ""}`))) && (
                    <div className="text-sm text-[#8A6D1A]" data-testid="booking-guestbook">• Video Messages Guestbook</div>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="text-sm text-[#5C5A56]">Package not found</div>
          )}
        </div>

        <div className="border-t border-[#E5E3DB] pt-6 mb-8 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5">
          <Item label="Deposit Paid">
            {paid ? `${fmtGBP(paid.amount)} ${String(paid.currency || "gbp").toUpperCase()}` : "No online deposit"}
          </Item>
          {paid?.session_id && (
            <Item label="Stripe Reference">
              <span className="break-all text-xs">{paid.session_id}</span>
            </Item>
          )}
          {pkg && paid && (
            <Item label="Balance Remaining">
              {fmtGBP(Math.max(0, Number(pkg.price) + (acc.addons || []).reduce((s, a) => s + Number(a.price), 0) - Number(paid.amount)))}
            </Item>
          )}
        </div>

        <div className="border-t border-[#E5E3DB] pt-6">
          <div className="text-[10px] tracking-overline uppercase text-[#8A8782] mb-2">
            Terms &amp; Conditions
          </div>
          {acc.terms_agreed_at ? (
            <p className="text-sm text-[#2E5E3A]" data-testid="terms-agreed-info">
              Agreed on {fmtTs(acc.terms_agreed_at)}
              {acc.terms_agreed_via === "email_link" ? " (via email link)" : " (during acceptance)"}
            </p>
          ) : (
            <p className="text-sm text-[#9A6B15]" data-testid="terms-missing-info">
              No terms agreement on record. This couple accepted before the
              terms step was introduced.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
